import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import EditIcon from '@mui/icons-material/Edit';
import { cloneDeep } from 'lodash';

import {
  DeleteOutlined,
  ExpandLessOutlined,
  ExpandMoreOutlined,
} from '@mui/icons-material';
import { Grid, IconButton } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import cx from 'classnames';
import styles from './Inputs.module.css';
import { ActionButton } from '../Buttons/Buttons';
import CustomModal from '../Modals/Modals';
import DeleteConfirmationModal from '../Modals/DeleteConfirmationModal';

// eslint-disable-next-line import/no-cycle
import BuildForm from '../../BuildForm/BuildForm'; // Known circular dependency which is required for SubForm to work
import DisplayErrorMessages from './DisplayErrorMessages';
import ModalDrawer from '../Layout/Drawer/ModalDrawer';

function SubForm({
  label,
  buttonText,
  subForm,
  onSave = () => {},
  formData,
  name,
  viewMode = false,
  idKey,
  disabled = false,
  deleteConfirmation = undefined,
  level = 0,
  errorText = null,
  getUploadUrl,
  getDownloadUrl,
}) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editIndex, setEditIndex] = useState(-1);
  const [newDocumentId, setNewDocumentId] = useState(uuidv4());
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isMultiStep, setIsMultiStep] = useState(false);
  const [tmpFormData, setTmpFormData] = useState([]);
  const [deleteIndex, setDeleteIndex] = useState(-1);

  useEffect(() => {
    if (Array.isArray(subForm)) {
      setIsMultiStep(true);
      setTmpFormData([].fill({}, 0, subForm.length));
    }
  }, []);

  const [currentStep, setCurrentStep] = useState(0);

  const isLastSubFromStep = useMemo(
    () => currentStep === subForm.length - 1,
    [currentStep, subForm],
  );

  const isEditMode = useMemo(
    () => editMode && editIndex !== -1,
    [editMode, editIndex],
  );

  const openModal = () => {
    if (!newDocumentId) setNewDocumentId(uuidv4());
    setTmpFormData([].fill({}, 0, subForm.length));
    setOpen(true);
  };

  const openEditModal = (index, id) => {
    setNewDocumentId(id);
    setEditIndex(index);
    setEditMode(true);
    setOpen(true);
  };

  const handleDelete = (index) => {
    const newFormData = { ...formData };
    newFormData[name].splice(index, 1);
    onSave(name, newFormData[name]);
    setExpanded([...expanded.slice(0, index), ...expanded.slice(index + 1)]);
  };

  return (
    <>
      <div>
        <div
          className={cx(styles.labelColumn, {
            [styles.labelError]: !!errorText,
            error: !!errorText,
          })}
        >
          {label}
        </div>
      </div>
      {!viewMode && (
        <div className={styles.subFormButton}>
          {!viewMode && (
            <>
              <ActionButton onClick={openModal}>{buttonText}</ActionButton>
              <DisplayErrorMessages error={!!errorText} errorText={errorText} />
            </>
          )}
          <DeleteConfirmationModal
            title={deleteConfirmation?.title || 'DELETE CONFIRMATION'}
            body={
              deleteConfirmation?.text ||
              'Are you sure you want to delete this?'
            }
            open={deleteModalOpen}
            onClose={() => setDeleteModalOpen(false)}
            onConfirm={() => {
              handleDelete(deleteIndex);
              setDeleteModalOpen(false);
              setDeleteIndex(-1);
            }}
          />
          <CustomModal
            open={open}
            handleClose={(_event, reason) => {
              if (reason && reason === 'backdropClick') return;
              setOpen(false);
              setEditMode(false);
              setEditIndex(-1);
              setNewDocumentId(null);
              setCurrentStep(0);
              if (isMultiStep) {
                setTmpFormData([].fill({}, 0, subForm.length));
              }
            }}
            size={isMultiStep ? 'large' : 'medium'}
            level={level}
          >
            {isMultiStep ? (
              <Grid
                container
                spacing={1}
                style={{
                  overflowY: 'hidden',
                }}
              >
                <Grid item xs={12} md={3} className={styles.limitSize}>
                  <ModalDrawer
                    stepper={{
                      steps: subForm.map((step) => step.pageTitle),
                      currentStep,
                      displayedStep: currentStep,
                      activeStep: currentStep,
                      editMode: false,
                      approvalMode: false,
                    }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={9}
                  style={{
                    overflowY: 'hidden',
                  }}
                >
                  {subForm.map((form, index) => (
                    <div
                      key={JSON.stringify(form)}
                      style={{
                        display: currentStep === index ? 'block' : 'none',
                      }}
                    >
                      <BuildForm
                        formPage={form}
                        buttonText={isLastSubFromStep ? 'save' : 'continue'}
                        level={level + 1}
                        onSubmit={(data) => {
                          if (isLastSubFromStep) {
                            if (isEditMode) {
                              const newFormData = { ...cloneDeep(formData) };
                              newFormData[name][editIndex] = [
                                ...tmpFormData,
                                { ...data, id: newDocumentId },
                              ];
                              onSave(name, newFormData[name]);
                              setEditMode(false);
                              setEditIndex(-1);
                            } else {
                              onSave(name, [
                                ...cloneDeep(formData[name]),
                                [
                                  ...tmpFormData,
                                  { ...data, id: newDocumentId },
                                ],
                              ]);
                            }
                            setExpanded([...expanded, false]);
                          } else {
                            tmpFormData.push({
                              id: newDocumentId,
                              ...data,
                            });
                            setNewDocumentId(uuidv4());
                            setCurrentStep(currentStep + 1);
                          }
                          if (isLastSubFromStep) {
                            setCurrentStep(0);
                            setNewDocumentId(null);
                            setTmpFormData([]);
                            setOpen(false);
                          }
                        }}
                        formsData={
                          isEditMode
                            ? formData[name][editIndex][currentStep] || {}
                            : {}
                        }
                        nested
                        locked={disabled}
                        getUploadUrl={getUploadUrl}
                        getDownloadUrl={getDownloadUrl}
                      />
                    </div>
                  ))}
                </Grid>
              </Grid>
            ) : (
              <BuildForm
                formPage={subForm}
                buttonText="save"
                level={level + 1}
                onSubmit={(data) => {
                  if (isEditMode) {
                    const newFormData = { ...cloneDeep(formData) };
                    newFormData[name][editIndex] = {
                      ...data,
                      id: newDocumentId,
                    };
                    onSave(name, newFormData[name]);
                    setEditMode(false);
                    setEditIndex(-1);
                  } else {
                    const newFormData = { ...cloneDeep(formData) };
                    newFormData[name].push({ id: newDocumentId, ...data });
                    onSave(name, newFormData[name]);
                    setExpanded([...expanded, false]);
                  }
                  setOpen(false);
                }}
                formsData={isEditMode ? formData[name][editIndex] : {}}
                nested
                locked={disabled}
                getUploadUrl={getUploadUrl}
                getDownloadUrl={getDownloadUrl}
              />
            )}
          </CustomModal>
        </div>
      )}
      <div className={styles.nested}>
        {Array.isArray(formData[name]) &&
          formData[name].map((item, index) => (
            <div key={JSON.stringify(item)} className={styles.subFormView}>
              <div className={styles.subFormViewHeader}>
                <div className={styles.leftSection}>
                  <IconButton
                    aria-label="expand"
                    onClick={() => {
                      const newExpanded = [...expanded];
                      newExpanded[index] = !newExpanded[index];
                      setExpanded(newExpanded);
                    }}
                    size="large"
                  >
                    {(expanded[index] && <ExpandMoreOutlined />) || (
                      <ExpandLessOutlined />
                    )}
                  </IconButton>
                  <span>
                    {(isMultiStep && item[0] ? item[0][idKey] : item[idKey]) ||
                      item.id}
                  </span>
                </div>
                {!viewMode && (
                  <div className={styles.rightSection}>
                    <IconButton
                      aria-label="edit"
                      onClick={() =>
                        openEditModal(
                          index,
                          isMultiStep ? item[currentStep].id : item.id,
                        )
                      }
                      size="large"
                    >
                      <EditIcon className={styles.icon} />
                    </IconButton>
                    <IconButton
                      aria-label="delete"
                      onClick={() => {
                        if (deleteConfirmation) {
                          setDeleteModalOpen(true);
                          setDeleteIndex(index);
                        } else handleDelete(index);
                      }}
                      size="large"
                    >
                      <DeleteOutlined className={styles.icon} />
                    </IconButton>
                  </div>
                )}
              </div>
              <div className={expanded[index] ? '' : styles.hidden}>
                {Array.isArray(subForm) ? (
                  subForm.map((sf, idx) => (
                    <BuildForm
                      key={sf.pageTitle}
                      formPage={sf}
                      onSubmit={() => {}}
                      formsData={item[idx] || {}}
                      buttonText=""
                      viewMode
                      nested
                      locked={disabled}
                      getUploadUrl={getUploadUrl}
                      getDownloadUrl={getDownloadUrl}
                    />
                  ))
                ) : (
                  <BuildForm
                    formPage={subForm}
                    onSubmit={() => {}}
                    formsData={item}
                    buttonText=""
                    viewMode
                    nested
                    locked={disabled}
                    getUploadUrl={getUploadUrl}
                    getDownloadUrl={getDownloadUrl}
                  />
                )}
              </div>
            </div>
          ))}
      </div>
    </>
  );
}

SubForm.propTypes = {
  label: PropTypes.string.isRequired,
  buttonText: PropTypes.string.isRequired,
  subForm: PropTypes.oneOfType([
    PropTypes.shape({}).isRequired,
    PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  ]).isRequired,
  onSave: PropTypes.func,
  formData: PropTypes.shape({}).isRequired,
  name: PropTypes.string.isRequired,
  viewMode: PropTypes.bool,
  idKey: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  deleteConfirmation: PropTypes.shape({
    title: PropTypes.string,
    text: PropTypes.string,
  }),
  errorText: PropTypes.arrayOf(PropTypes.string),
  getUploadUrl: PropTypes.func.isRequired,
  getDownloadUrl: PropTypes.func.isRequired,
  level: PropTypes.number,
};

export default SubForm;
